import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Eye, KeyRound, Loader2, MapPin, Truck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';

const KEYS = [
  {
    name: 'SHIPPO_API_KEY',
    title: 'Shippo',
    icon: Truck,
    blurb: 'Buys labels and quotes rates. Nothing ships without it.',
    help: 'Shippo dashboard → Settings → API. A test key starts shippo_test_, a live one shippo_live_.',
    placeholder: 'shippo_live_…',
  },
  {
    name: 'EASYPOST_API_KEY',
    title: 'EasyPost',
    icon: Truck,
    blurb: 'An alternative to Shippo that also sells First-Class letters and flats and Library Mail.',
    help: 'EasyPost dashboard → API Keys. A test key starts EZTK, a production key EZAK.',
    placeholder: 'EZAK…',
  },
  {
    name: 'GOOGLE_MAPS_API_KEY',
    title: 'Google Places',
    icon: MapPin,
    blurb: 'Looks up and checks delivery addresses on the Mail page.',
    help: 'Google Cloud console → APIs & Services → Credentials. Two APIs matter and are enabled separately: Places API (New) for the address lookup, and Address Validation API for the check that the place exists.',
    placeholder: 'AIza…',
  },
];

/**
 * API keys, set here rather than by editing a file on the server.
 *
 * Write-only by design. The Base44 build kept the Shippo key where any signed-in
 * user could read it back; here the app will tell you a key is set and show its
 * last four characters, and nothing more. There is no endpoint that returns one.
 */
export default function ApiKeysSection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['secrets'],
    queryFn: () => api.request('/secrets'),
  });

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="w-5 h-5" />
          API keys
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <Alert className="border-slate-200 bg-slate-50">
          <Eye className="h-4 w-4 text-slate-600" />
          <AlertDescription className="text-slate-700">
            Keys can be changed here but never read back — the app will only ever show you the last
            four characters. Every change is recorded in Activity. Keys take effect immediately;
            there is no need to restart anything.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          KEYS.map((config) => (
            <ApiKeyRow
              key={config.name}
              config={config}
              status={(data?.secrets ?? []).find((s) => s.name === config.name)}
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['secrets'] });
                // Autocomplete availability is derived from the key, so the Mail
                // page must not keep believing the old answer.
                queryClient.invalidateQueries({ queryKey: ['mapsStatus'] });
                queryClient.invalidateQueries({ queryKey: ['version'] });
                queryClient.invalidateQueries({ queryKey: ['shippingProvider'] });
              }}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function ApiKeyRow({ config, status, onChanged }) {
  const [value, setValue] = useState('');
  const Icon = config.icon;

  const saveMutation = useMutation({
    mutationFn: (key) => api.request(`/secrets/${config.name}`, { method: 'PUT', body: { value: key } }),
    onSuccess: (result) => {
      setValue('');
      onChanged();
      // A warning means the key is good but part of what it is for is not
      // switched on - worth more than a couple of seconds on screen.
      if (result?.warning) toast.warning(result.warning, { duration: 15000 });
      else toast.success(`${config.title} key saved and verified (…${result.hint})`);
    },
    onError: (error) => toast.error(error.message ?? 'Could not save that key'),
  });

  const testMutation = useMutation({
    mutationFn: () => api.request(`/secrets/${config.name}/test`, { method: 'POST' }),
    onSuccess: (result) => {
      if (result?.warning) toast.warning(result.warning, { duration: 15000 });
      else toast.success(`${config.title} key works`);
    },
    onError: (error) => toast.error(error.message ?? 'That key did not work'),
  });

  const removeMutation = useMutation({
    mutationFn: () => api.request(`/secrets/${config.name}`, { method: 'DELETE' }),
    onSuccess: (result) => {
      onChanged();
      toast.success(result.message ?? 'Key removed');
    },
    onError: (error) => toast.error(error.message ?? 'Could not remove that key'),
  });

  const configured = status?.configured ?? false;

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-slate-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">{config.title}</span>
              {configured ? (
                <Badge className="bg-green-100 text-green-800 border-green-300 gap-1 hover:bg-green-100">
                  <CheckCircle2 className="w-3 h-3" />
                  ending {status.hint}
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <XCircle className="w-3 h-3" />
                  not set
                </Badge>
              )}
              {/* Worth surfacing: a key coming from the server environment will
                  survive clearing it here, which is otherwise baffling. */}
              {status?.source === 'environment' && (
                <Badge variant="outline" className="text-xs">
                  from server config
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-0.5">{config.blurb}</p>
          </div>
        </div>

        {configured && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
            </Button>
            {status?.source === 'settings' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm(`Remove the ${config.title} key?`)) removeMutation.mutate();
                }}
                disabled={removeMutation.isPending}
                className="text-red-700 border-red-200 hover:bg-red-50"
              >
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          saveMutation.mutate(value);
        }}
        className="space-y-2"
      >
        <Label htmlFor={`key-${config.name}`} className="text-xs">
          {configured ? 'Replace with a new key' : 'Enter the key'}
        </Label>
        <div className="flex gap-2">
          <Input
            id={`key-${config.name}`}
            // `password` so it is not shoulder-surfed at the counter, and so
            // browsers do not offer to remember it as a normal field.
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={config.placeholder}
            className="font-mono"
          />
          <Button
            type="submit"
            disabled={!value.trim() || saveMutation.isPending}
            className="bg-slate-800 hover:bg-slate-900 shrink-0"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-500">{config.help}</p>
      </form>
    </div>
  );
}
